import { commonOptionsAtom } from '@/atoms/commonOptions';
import { FORMAT_TIME } from '@/types/common';
import { UserMngItem, UserStatus } from '@/types/userMng';
import { formatAttribution, formatCampaign } from '@/utils/attribution';
import { Button, Popconfirm, Space, TableColumnType, Tag } from 'antd';
import dayjs from 'dayjs';
import { useAtomValue } from 'jotai';
import { useNavigate } from 'react-router-dom';
import { LimitEnum } from '../type';

// 用户来源类型映射
const SourceTypeMap = new Map<number, string>([
  [1, 'suppart'],
  [2, 'appleId'],
  [3, 'facebook'],
  [4, 'googleId'],
  [5, 'LineId'],
]);

// 设备类型映射
const DeviceTypeMap = new Map<number, string>([
  [1, 'IOS APP'],
  [2, 'Android APP'],
  [3, 'iOS H5'],
  [4, 'Android H5'],
  [5, 'Other/PC'],
]);

interface TableColumnsProps {
  onDetail: (userId: string) => void;
  onHistory: (userId: string) => void;
  onAlert: (record: UserMngItem) => void;
  onDeAlert: (record: UserMngItem) => void;
  onBan: (record: UserMngItem) => void;
  onUnban: (record: UserMngItem) => void;
  onInviteNumClick: (userNumber: number) => void;
  onLimitChange: (userIds: string, enableLimit: number) => void;
  onGiveVip: (record: UserMngItem) => void;
}

export const useTableColumns = ({
  onDetail,
  onHistory,
  onAlert,
  onDeAlert,
  onBan,
  onUnban,
  onInviteNumClick,
  onLimitChange,
  onGiveVip,
}: TableColumnsProps): TableColumnType<UserMngItem>[] => {
  const navigate = useNavigate();
  // const renderAlertAction = (record: UserMngItem) => {
  //   if (record.userStatus === UserStatus.ALERT) {
  //     return (
  //       <Button type="link" size="small" onClick={() => onDeAlert(record)}>
  //         解除警告
  //       </Button>
  //     );
  //   }
  //   return (
  //     <Button type="link" size="small" onClick={() => onAlert(record)} danger>
  //       警告
  //     </Button>
  //   );
  // };

  // const renderAction = (record: UserMngItem) => {
  //   if (record.userStatus === UserStatus.BANNED) {
  //     return (
  //       <Button type="link" size="small" onClick={() => onUnban(record)}>
  //         解封
  //       </Button>
  //     );
  //   }
  //   return (
  //     <Button type="link" size="small" danger onClick={() => onBan(record)}>
  //       封禁
  //     </Button>
  //   );
  // };

  const membership = (record: UserMngItem) => {
    return (
      <Button 
        type="link" 
        size="small"
        onClick={() => onGiveVip(record)}
      >
        赠送会员
      </Button>
    );
  };

  const rendeLimitAction = (record: UserMngItem) => {
    if (record.enableLimit === LimitEnum.noLimit) {
      return (
        <Popconfirm
          title="确定要开启限额吗"
          onConfirm={() => onLimitChange(record.userId, LimitEnum.limit)}
          okText="确定"
          cancelText="取消"
        >
          <Button type="link" size="small" danger >
            限额开启
          </Button>
        </Popconfirm>
      );
    }
    if (record.enableLimit === LimitEnum.limit) {
      return (
        <Popconfirm
          title="确定要关闭限额吗"
          onConfirm={() => onLimitChange(record.userId, LimitEnum.noLimit)}
          okText="确定"
          cancelText="取消"
        >
          <Button type="link" size="small">
            限额关闭
          </Button>
        </Popconfirm>
      );
    }
    return null;
  };

  const commonOptions = useAtomValue(commonOptionsAtom);

  return [
    {
      title: 'Supp ID',
      dataIndex: 'userNumber',
      width: 100,
    },
    {
      title: '用户名',
      dataIndex: 'name',
      width: 120,
      render: (value: string) => {
        return <span>{value || '-'}</span>;
      },
    },
    {
      title: 'email',
      dataIndex: 'email',
      width: 160,
      render: (value: string) => {
        return <span>{value || '-'}</span>;
      },
    },
    {
      title: '注册时间',
      dataIndex: 'createTime',
      width: 160,
      render: (value: string) => {
        return <span>{dayjs(value).format(FORMAT_TIME)}</span>;
      },
    },
    {
      title: '状态',
      dataIndex: 'userStatusDesc',
      width: 100,
      render: (_, record) => {
        return (
          <Tag
            color={record.userStatus === UserStatus.ACTIVE ? 'green' : 'red'}
          >
            {record.userStatusDesc}
          </Tag>
        );
      },
    },
    {
      title: '是否订阅',
      dataIndex: 'subscriptionStatus',
      width: 100,
      render: (_, record) => {
        return (
          <Tag
            color={record.subscriptionStatus ? 'green' : 'red'}
          >
            {record.subscriptionStatus ? "是" : "否"}
          </Tag>
        );
      },
    },
    {
      title: '用户类型',
      dataIndex: 'userType',
      width: 100,
      render: (value: number) => {
        const userType = commonOptions.userTypes.find(
          (type) => type.value === value
        );
        return <span>{userType?.text || '-'}</span>;
      },
    },
    {
      title: '用户来源',
      dataIndex: 'sourceType',
      width: 100,
      render: (value: number) => {
        return <span>{value ? SourceTypeMap.get(value) || '-' : '-'}</span>;
      },
    },
    {
      title: '设备类型',
      dataIndex: 'deviceType',
      width: 100,
      render: (value: number) => {
        return <span>{value ? DeviceTypeMap.get(value) || '-' : '-'}</span>;
      },
    },
    {
      title: '渠道来源',
      dataIndex: 'afSource',
      width: 120,
      render: (value: string) => formatAttribution(value),
    },
    {
      title: '归因数据',
      dataIndex: 'afCampaign',
      width: 120,
      render: (value: string) => formatCampaign(value),
    },
    {
      title: '艺术家',
      dataIndex: 'isArtist',
      width: 80,
      render: (value: boolean) => (
        <Tag color={value ? 'blue' : 'default'}>{value ? '是' : '否'}</Tag>
      ),
    },
    {
      title: '认证学校',
      dataIndex: 'campusName',
      width: 120,
      render: (value: string) => value || '-',
    },
    {
      title: '推荐人',
      dataIndex: 'invitorName',
      width: 120,
      render: (value: string) => value || '-',
    },
    {
      title: '邀请人数',
      dataIndex: 'inviteUserNum',
      width: 100,
      render: (value: number, record) => (
        <Button
          type="link"
          size="small"
          onClick={() => onInviteNumClick(record.userNumber)}
        >
          {value}
        </Button>
      ),
    },
    {
      title: '作品数',
      dataIndex: 'artworkNum',
      width: 80,
    },
    {
      title: '新手任务',
      dataIndex: 'isTaskFinished',
      width: 80,
      render: (value: boolean) => (
        <Tag color={value ? 'success' : 'default'}>
          {value ? '已完成' : '未完成'}
        </Tag>
      ),
    },
    {
      title: '最近操作人',
      dataIndex: 'updateUser',
      width: 100,
    },
    {
      title: '操作',
      key: 'actions',
      width: 100,
      fixed: 'right',
      render: (_, record) => (
        <Space size="small" direction="vertical">
          <Button
            type="link"
            size="small"
            onClick={() => onDetail(record.userId)}
          >
            查看详情
          </Button>
          <Button
            type="link"
            size="small"
            onClick={() => onHistory(record.userId)}
          >
            查看管理历史
          </Button>
          <Button
            type="link"
            size="small"
            onClick={() => navigate(`/user-mng/points/${record.userId}`)}
          >
            积分明细
          </Button>
          <Button
            type="link"
            size="small"
            onClick={() => navigate(`/user-mng/coins/${record.userId}`)}
          >
            硬币明细
          </Button>
          {/* {renderAlertAction(record)} */}
          {/* {renderAction(record)} */}
          {membership(record)}
          {rendeLimitAction(record)}
        </Space>
      ),
    },
  ];
};
